import React, { useMemo } from "react";
import FloatingViewMapButton from "../../util/FloatingViewMapButton";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { useTranslation } from "react-i18next";
import PriceTagMap, {
    PriceTagMarker,
} from "./PriceTagMap";
import { formatCurrency } from "../../../../../util";
import { OwnRoomPin, hasPin, roomIsPinned, withOwnRoom } from "./ownRoomMarker";
import { mapEmbedSrc } from "../../../../../util/mapEmbed";

const FULLSCREEN_MAP_OPTIONS: google.maps.MapOptions = {
    disableDefaultUI: true,
    clickableIcons: false,
    styles: [
        {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
        },
    ],
};

interface MapViewProps {
    /** The room page's own room: pinned when the results do not include it (see `withOwnRoom`). */
    ownRoom?: OwnRoomPin | null;
    /**
     * The room's address, mapped as text when no pin exists: the interactive
     * map has nothing to centre on without one and renders grey.
     */
    fallbackAddress?: Parameters<typeof mapEmbedSrc>[0];
}

const MapView: React.FC<MapViewProps> = ({ ownRoom, fallbackAddress }) => {
    const { t, i18n } = useTranslation();
    const { locationApartmentList, selectedApartment } = useSelector(
        (state: RootState) => state.apartment
    )!;

    const markers = useMemo<PriceTagMarker[]>(() => {
        if (!Array.isArray(locationApartmentList)) {
            return withOwnRoom([], ownRoom);
        }

        const selectedId = selectedApartment?.assetId || selectedApartment?.id || null;

        const fromResults = locationApartmentList
            .filter(
                (apt) =>
                    typeof apt?.geoLat === "number" &&
                    typeof apt?.geoLng === "number" &&
                    !Number.isNaN(apt.geoLat) &&
                    !Number.isNaN(apt.geoLng)
            )
            .map((apt) => {
                const priceValue = Number(apt?.price ?? 0);
                const hasPrice = !Number.isNaN(priceValue) && priceValue > 0;
                // #2936: the SAME formatter the list card uses. This used to
                // hand-build the label whenever the tenant config carried a
                // `currencySymbol` — symbol prepended, decimals dropped on a
                // whole number, and the result identical in every language.
                // A German screen therefore read `€70` on the pin next to
                // `70,00 €` on the card for the same room.
                //
                // `formatCurrency` already resolves the tenant's currency and
                // already puts the symbol after the number for EUR and NOK, so
                // the branch was not only wrong, it was redundant.
                const label = hasPrice
                    ? formatCurrency(priceValue, i18n.language)
                    : t("apartment.price_on_request");

                return {
                    id: apt.assetId || apt.id,
                    lat: apt.geoLat,
                    lng: apt.geoLng,
                    label,
                    price: hasPrice ? priceValue : null,
                    isSelected: selectedId ? selectedId === (apt.assetId || apt.id) : false,
                };
            });
        return withOwnRoom(fromResults, ownRoom);
    }, [locationApartmentList, selectedApartment, i18n.language, t, ownRoom]);

    // On a room page the room itself must be pinned (not only its
    // neighbours); otherwise the map of its address. The search map has no
    // room and no address and keeps the interactive map.
    const pinned = ownRoom ? roomIsPinned(markers, ownRoom) : hasPin(markers);
    const addressMapSrc = pinned ? null : mapEmbedSrc(fallbackAddress);

    return (
        <div
            style={{
                position: "fixed",
                width: "100%",
                height: "100vh",
                bottom: 0,
                overflow: "hidden",
            }}
        >
            {addressMapSrc ? (
                <iframe
                    title="Property Location"
                    className="absolute inset-0 w-full h-full border-0"
                    src={addressMapSrc}
                    allowFullScreen
                />
            ) : (
                <PriceTagMap
                    markers={markers}
                    mapOptions={FULLSCREEN_MAP_OPTIONS}
                    className="absolute inset-0"
                    fitBoundsPadding={{ top: 80, right: 80, bottom: 120, left: 80 }}
                    zoomOnSingleMarker={14}
                />
            )}

            <div
                style={{
                    position: "absolute",
                    bottom: "70px",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                }}
            >
                <FloatingViewMapButton isMapView />
            </div>
        </div>
    );
};

export default MapView;
